import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  additionalSection?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Legacy of Faith and Culture',
    content: [
      '<strong>Objective:</strong> Develop a sense of responsibility in preserving and transmitting spiritual and cultural heritage.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• The psychology of legacy and transmission',
      '• Teaching values through stories and rituals',
      '• Creating family traditions of gratitude and remembrance',
      '• Living as a bridge between past and future',
      '<br/>',
      '<strong>Discussion:</strong> Legacies are not just material – they are values, stories, and beliefs. Keeping traditions alive strengthens community identity and provides young people with grounding amid change.'
    ]
  },
  {
    title: 'The Psychology of Legacy',
    content: [
      'The psychology of legacy involves understanding how transmitting values and traditions fosters continuity and meaning.',
      '<br/>',
      'It builds intergenerational resilience.'
    ]
  },
  {
    title: 'Teaching Values Through Stories',
    content: [
      'Teaching values through stories and rituals preserves wisdom and connects generations.',
      '<br/>',
      'Stories provide emotional and moral guidance.'
    ]
  },
  {
    title: 'Creating Family Traditions',
    content: [
      'Creating family traditions of gratitude and remembrance strengthens bonds and cultural identity.',
      '<br/>',
      'These practices promote well-being across generations.'
    ]
  },
  {
    title: 'Living as a Bridge',
    content: [
      'Living as a bridge between past and future means honoring heritage while nurturing the next generation.',
      '<br/>',
      'It fosters a sense of purpose and continuity.'
    ]
  },
  {
    title: 'Final Activity',
    content: [
      'Design a Legacy Project: a written piece, artwork, or family ritual that celebrates a cultural or spiritual value you wish to pass on.'
    ],
    additionalSection: {
      title: 'Reflection',
      content: [
        'Consider your role in preserving and transmitting spiritual and cultural heritage.'
      ]
    }
  },
  {
    title: 'Key Takeaway',
    content: [
      'The deepest form of well-being is living a life that connects generations – honoring the past while nurturing the future.'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You have completed Module 6: Legacy of Faith and Culture! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about legacy and transmission.',
        'The quiz will help you review key concepts about preserving spiritual and cultural heritage.'
      ]
    }
  }
];

const ReligioTraditioCulturalModule6Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('rtcM6');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/religio-traditio-cultural-module-6/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/religio-traditio-cultural-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left religio-traditional-cultural-panel"
        >
          <div className="module-header">
            <h1>Module 6</h1>
            <p className="module-subtitle">Legacy of Faith and Culture</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Develop a sense of responsibility in preserving and transmitting spiritual and cultural heritage.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {safeCurrentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/religio-traditio-cultural-module-6/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReligioTraditioCulturalModule6Page;


